// Medición de áreas de una planta editada. Función pura, sin DOM: el editor la
// llama cuando el usuario toca «Recalcular áreas», nunca mientras arrastra.
//
// Se mide A EJES DE PARED: cada región llega hasta el eje de las paredes que la
// encierran, así que se queda con la mitad del grosor de cada una. No es un
// detalle de implementación, es como calcula la empresa —«a centros de pared, no
// área interna»— y es lo que hace que el número absoluto se sostenga solo, sin
// corregirlo contra el plano. Ver `anclar`.
//
// Se miden REGIONES CERRADAS, no recintos. Querer reproducir las zonas de
// Archicad desde las paredes es adivinar: una zona es dato que dibuja el
// arquitecto. La planta baja de muchos modelos es un solo espacio continuo
// —cocina, comedor y sala se comunican sin puerta— y decirlo así es más honesto
// que inventar un corte que el plano no tiene.

import type {
  Clase, Divisor, Estado, Libre, Orientacion, Pared, Planta, Rect, VistaObra,
} from './tipos';
import { mismaGeometria, seVe } from './tipos';

export type { Rect };

/** Lado de la celda de la retícula de inundación, en metros. */
const CELDA = 0.025;
/**
 * Hasta dónde se estira una punta para cerrar un encuentro —esquina, T o dos
 * pedazos de la misma pared—. Ver `sellar`.
 *
 * 45 cm es corto a propósito: una puerta mide 70 cm o más, así que el tope
 * nunca alcanza para cerrar un vano real.
 */
const TOPE_ESQUINA = 0.45;
/**
 * Área mínima para que una región cuente como recinto, en m². Debajo de esto no
 * hay cuarto: son las ranuras que quedan entre dos pedazos de pared extraídos,
 * y listarlas como «Sin rótulo · 0,04 m²» sería ruido.
 */
const MINIMA = 0.2;

export interface Region {
  nombre: string;
  /**
   * El nombre como lo escribiría un PLANO: los nombres distintos de los
   * recintos que caen adentro, unidos con « + », o **vacío** si no hay ninguno.
   *
   * No es `nombre` con otro envoltorio. `nombre` tiene que ser una llave única
   * y no vacía —el editor indexa el reposo por ahí—, y para lograrlo se le
   * agrega «Sin rótulo» cuando nadie nombró la región y un «1/2/3» cuando dos
   * se llaman igual. Eso es andamiaje del editor: una lámina que se entrega a
   * un cliente no quiere ninguno de los dos inventos, y con `rotulo` puede
   * callarse y dejar que la región hable con su área.
   */
  rotulo: string;
  /**
   * El área de la región, medida A EJES DE PARED: la región llega hasta el eje
   * de cada pared que la encierra, así que se queda con la mitad del grosor de
   * cada una. Es como calcula la empresa.
   *
   * Es la geometría y nada más —no lleva corrección contra el plano—. Ver
   * `anclar` para por qué la corrección se fue.
   */
  area: number;
  /** Su área en reposo. La diferencia contra `area` es lo que ganó o perdió. */
  referencia: number;
  x: number;
  y: number;
  /**
   * El índice que le puso la inundación. Renumera en cuanto alguien mueve una
   * pared, así que NO sirve para recordar una región entre dos mediciones —para
   * eso está el nombre—, pero sí para saber qué rótulos le caen adentro AHORA:
   * es el mismo número que queda en `Recinto.region`.
   */
  indice: number;
  /**
   * La forma de la región, en rectángulos. Es lo que deja al editor pintarla y
   * saber si el dedo cayó adentro sin tener que publicar la retícula entera ni
   * volver a inundar nada.
   */
  celdas: Rect[];
  /** De qué lado suma. Una región cerrada es `interna` salvo que el usuario la
   *  haya marcado al revés en `Planta.clases`. */
  clase: Clase;
}

export interface Medicion {
  regiones: Region[];
  /** Recintos cuyo rótulo quedó fuera de toda región cerrada: el usuario abrió
   *  el cuarto al exterior. Es un aviso, no un error. */
  sueltos: string[];
  /** Lo que suma del lado INTERNO: es `interna`, con el nombre viejo. Es el
   *  número que se compara contra el modelo. */
  total: number;
  /** El área en reposo: lo que mide la planta a ejes sin tocarle una pared. */
  referencia: number;
  /** Diferencia contra la referencia. Es lo que fija el precio. */
  delta: number;
  /** Las áreas dibujadas a mano, medidas por su propia geometría. Nacen
   *  `externa` —una terraza no entra en el ÁREA INTERNA— pero la clase la manda
   *  el usuario, así que una de ellas puede estar sumando adentro. */
  libres: Array<{ id: number; nombre: string; area: number; clase: Clase }>;
  /**
   * Las dos sumas que pidió Daniel, cada una sobre TODO lo que tenga esa clase
   * —regiones cerradas y áreas dibujadas a mano juntas—.
   *
   * La separación sigue siendo el dato: `interna` es lo que se compara contra el
   * cajetín y su diferencia fija el precio; `externa` se mide y se muestra pero
   * no entra ahí. Lo que cambió es que ahora quién va de cada lado lo decide el
   * usuario y no el tipo de objeto.
   */
  interna: number;
  externa: number;
  /**
   * Las otras dos columnas, desde el 7-oct-2026: «cuatro columnas
   * independientes: Interna · Externa · Lote · Azotea, y sólo Interna sigue
   * fijando el precio».
   *
   * INDEPENDIENTES quiere decir que no se reparten entre `interna` y `externa`:
   * un lote de 90 m² metido en `externa` no movería el precio pero sí la cifra
   * que el vendedor lee como área externa, y la azotea se cobra aparte. Las
   * cuatro se suman sobre TODO lo que tenga esa clase —regiones cerradas y
   * áreas dibujadas a mano juntas—, igual que las dos de antes.
   *
   * `total`, `referencia` y `delta` siguen leyendo SÓLO `interna`.
   */
  lote: number;
  azotea: number;
}

/** Área de un polígono por la fórmula del zapatero. Sirve para los cóncavos,
 *  que es lo que son una L, una T y el lote de 7-8 vértices del croquis. */
export function areaPoligono(vs: Array<[number, number]>): number {
  if (vs.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < vs.length; i++) {
    const [x0, y0] = vs[i];
    const [x1, y1] = vs[(i + 1) % vs.length];
    s += x0 * y1 - x1 * y0;
  }
  return Math.abs(s) / 2;
}

/** Caja de una pared (o de un tramo suyo) en metros: [x0, y0, x1, y1]. */
export function caja(w: Pared, a = w.a, b = w.b): [number, number, number, number] {
  return w.o === 'v'
    ? [w.c - w.e / 2, a, w.c + w.e / 2, b]
    : [a, w.c - w.e / 2, b, w.c + w.e / 2];
}

/**
 * Tramos macizos de una pared: lo que queda al descontarle los vanos.
 *
 * Se ordena acá y no se exige ordenado al que guarda: mover una puerta a lo
 * largo de su pared la pasa de lugar en la lista, y un barrido que da por hecho
 * el orden se comía el tramo de en medio.
 */
export function tramos(w: Pared): Array<[number, number]> {
  const t: Array<[number, number]> = [];
  let cur = w.a;
  for (const v of [...w.vanos].sort((p, q) => p.a - q.a)) {
    if (v.a > cur) t.push([cur, v.a]);
    cur = Math.max(cur, v.b);
  }
  if (w.b > cur) t.push([cur, w.b]);
  return t.length ? t : [[w.a, w.b]];
}

/**
 * Los cuadrados de esquina que hay que RELLENAR para que un codo no se dibuje
 * con una muesca.
 *
 * Las puntas aterrizan en el EJE de la perpendicular —es lo que hace la
 * Intersección y lo que deja la cota en su medida—, así que cada pared cubre
 * sólo SU mitad del cuadrado donde se cruzan y queda un cuadrante de medio
 * espesor por medio espesor sin pintar, justo en la esquina de AFUERA. Daniel
 * lo reportó dos veces con nombres distintos: el «escalón» de la esquina, y
 * después el sobrepaso que aparecía al taparlo moviendo las puntas a la cara
 * lejana. Son el mismo codo visto de los dos lados, y la única salida que no
 * rompe ninguna de las dos es ésta: la geometría se queda en el eje y el
 * cuadrado lo cierra el PINTOR.
 *
 * Se devuelve el cuadrado ENTERO y no el cuadrante huérfano: el resto ya lo
 * pintan las dos paredes y superponer no cuesta nada, mientras que recortar el
 * cuadrante exacto serían cuatro casos por esquina.
 *
 * No se rellena cuando una de las dos cruza el cuadrado de lado a lado —ahí no
 * hay muesca, es una T— ni cuando un vano cae en la esquina: tapar eso sería
 * tapiar una puerta.
 */
export function cuadradosDeEsquina(paredes: Pared[]): Array<[number, number, number, number]> {
  const EPS = 1e-6;
  const out: Array<[number, number, number, number]> = [];
  const hs = paredes.filter((w) => w.o === 'h');
  for (const v of paredes) {
    if (v.o !== 'v') continue;
    const vx0 = v.c - v.e / 2, vx1 = v.c + v.e / 2;
    const vy0 = Math.min(v.a, v.b), vy1 = Math.max(v.a, v.b);
    for (const h of hs) {
      const hy0 = h.c - h.e / 2, hy1 = h.c + h.e / 2;
      const hx0 = Math.min(h.a, h.b), hx1 = Math.max(h.a, h.b);
      // Cuánto del cuadrado tapa cada una CON SU LARGO.
      const ix = Math.min(vx1, hx1) - Math.max(vx0, hx0);
      const iy = Math.min(vy1, hy1) - Math.max(vy0, hy0);
      if (ix <= EPS || iy <= EPS) continue;                            // ni se tocan
      if (vx1 - vx0 - ix <= EPS || hy1 - hy0 - iy <= EPS) continue;    // ya está lleno
      if (v.vanos.some((q) => Math.min(q.b, hy1) - Math.max(q.a, hy0) > EPS)) continue;
      if (h.vanos.some((q) => Math.min(q.b, vx1) - Math.max(q.a, vx0) > EPS)) continue;
      out.push([vx0, hy0, vx1, hy1]);
    }
  }
  return out;
}

/**
 * Limpieza de encuentros: estira la punta de cada pared hasta el EJE de la
 * perpendicular con la que se topa.
 *
 * Archicad dibuja el vértice donde dos paredes doblan como parte del contorno y
 * no como relleno, así que al extraer queda un hueco de unos 20 cm justo en la
 * esquina. Y en un encuentro en T la extracción corta el tabique en la CARA de
 * la pared maestra, que a ejes está media pared antes del eje. En los dos casos
 * la inundación se escapa por el pelo que falta.
 *
 * Midiendo a paño eso lo tapaba la masa pintada —12 cm de ancho perdonan
 * mucho—, y por eso el sellado original sólo miraba esquinas. A ejes no hay masa
 * que perdone: medido sobre el catálogo, las plantas con algún recinto abierto
 * al exterior pasaban de 19 a 47 sin este arreglo.
 *
 * Dos condiciones, las dos necesarias:
 *
 * 1. La punta de `w` cae a menos de 45 cm del eje de `q`. Es lo que distingue
 *    «le faltó un pelo» de «acá hay un vano»: una puerta mide 70 cm o más, así
 *    que el tope nunca alcanza para cerrarla.
 * 2. El eje de `w` cae dentro del largo de `q` (con el mismo margen). Sin esto
 *    se inventaría una esquina entre dos paredes que no se cruzan.
 *
 * Sólo se estira `w`: la pared maestra de una T no necesita nada. En una esquina
 * de verdad las dos puntas se tocan, así que el par contrario también entra y
 * las dos se estiran.
 */
function sellar(ws: Pared[]): Pared[] {
  const extra: Pared[] = [];
  let id = -1;
  for (const w of ws) {
    for (const q of ws) {
      if (w.o !== q.o) {
        if (w.c < q.a - TOPE_ESQUINA || w.c > q.b + TOPE_ESQUINA) continue;
        for (const p of [w.a, w.b]) {
          if (Math.abs(p - q.c) > TOPE_ESQUINA) continue;
          extra.push({
            id: id--, o: w.o, c: w.c, e: w.e,
            a: Math.min(p, q.c), b: Math.max(p, q.c), vanos: [],
          });
        }
        continue;
      }
      // Dos pedazos de la MISMA pared física. La extracción corta una pared en
      // varios trozos y a veces los deja con el eje corrido un par de
      // centímetros: pintados se solapaban y sellaban, a ejes son dos líneas
      // paralelas con un escalón por donde se cuela la inundación. Se puentean
      // cuando los ejes están a menos de media pared y el salto entre puntas es
      // corto —el mismo tope que las esquinas, que no alcanza para una puerta—.
      if (w === q || Math.abs(w.c - q.c) > Math.max(w.e, q.e) / 2) continue;
      if (q.a - w.b > TOPE_ESQUINA || w.a - q.b > TOPE_ESQUINA) continue;
      extra.push({
        id: id--, o: w.o, c: (w.c + q.c) / 2, e: Math.max(w.e, q.e),
        a: Math.min(w.a, q.a), b: Math.max(w.b, q.b), vanos: [],
      });
    }
  }
  return ws.concat(extra);
}

/**
 * El eje de cada pared, como línea que corta el paso.
 *
 * Es la pieza con la que se mide A EJES: ver el comentario largo en
 * `medirPlanta`, donde está la decisión. Se saca a función porque la varita
 * inunda con las MISMAS barreras —si cada una armara sus ejes por su cuenta, la
 * forma que levanta la varita y la que mide la casa podrían no coincidir, que es
 * justo lo que no puede pasar: las dos salen en la misma tabla.
 */
function ejesDe(ws: Pared[]): Divisor[] {
  return ws.map((w, i) => (w.o === 'v'
    ? { id: -1000 - i, x1: w.c, y1: w.a - w.e / 2, x2: w.c, y2: w.b + w.e / 2 }
    : { id: -1000 - i, x1: w.a - w.e / 2, y1: w.c, x2: w.b + w.e / 2, y2: w.c }));
}

/** De qué lado de una recta cae un punto, contando el CERO como un lado fijo.
 *
 *  Lo del cero no es un detalle: un divisor trazado con imán cae justo encima de
 *  los centros de una fila entera de celdas, y con el signo estricto habitual
 *  (`d > 0`) esas celdas no quedan de ningún lado — se les bloqueaban las cuatro
 *  aristas y cada una se volvía una región de 6 cm² que `MINIMA` descarta. Un
 *  divisor de 5 m así borraba 0,125 m² de la casa. Mandando el cero siempre al
 *  mismo lado, la línea se comporta como el borde de la fila y no se pierde
 *  ninguna celda. */
const lado = (d: number) => d >= 0;

const cruz = (ax: number, ay: number, bx: number, by: number) => ax * by - ay * bx;

/** Cuánto se estira cada punta del divisor al rasterizarlo. Es una celda: tapa
 *  el redondeo de la punta contra la pared sin alcanzar para cerrar nada real
 *  —cae dentro del muro, que ya está marcado—. */
const PUNTA = CELDA;

/**
 * Las aristas de la retícula que un divisor no deja cruzar.
 *
 * Bloquea el PASO entre dos celdas vecinas, no las celdas: por eso dividir un
 * cuarto en dos no le quita área a nadie y las dos mitades suman exacto lo que
 * sumaba el entero. Marcarlo como muro —que es lo obvio— le robaría a la casa
 * una franja de 2,5 cm por todo el largo de la línea.
 *
 * `bloqH[j·W+i]` corta entre (i,j) y (i+1,j); `bloqV[j·W+i]`, entre (i,j) y
 * (i,j+1). Sólo se recorre la caja del divisor, así que una línea corta no
 * cuesta nada aunque la planta sea grande.
 */
function barreras(
  divisores: Divisor[], W: number, H: number, x0: number, y0: number,
): { bloqH: Uint8Array; bloqV: Uint8Array } | null {
  if (!divisores.length) return null;
  const bloqH = new Uint8Array(W * H);
  const bloqV = new Uint8Array(W * H);
  const cx = (i: number) => x0 + i * CELDA;
  const cy = (j: number) => y0 + j * CELDA;

  for (const d of divisores) {
    const dx = d.x2 - d.x1, dy = d.y2 - d.y1;
    const n = Math.hypot(dx, dy);
    if (n < CELDA) continue;   // un toque no es una línea
    // Estirada por las dos puntas, para que no se escape por el redondeo.
    const ex = (dx / n) * PUNTA, ey = (dy / n) * PUNTA;
    const ax = d.x1 - ex, ay = d.y1 - ey, bx = d.x2 + ex, by = d.y2 + ey;

    const ia = Math.max(0, Math.floor((Math.min(ax, bx) - x0) / CELDA) - 1);
    const ib = Math.min(W - 1, Math.ceil((Math.max(ax, bx) - x0) / CELDA) + 1);
    const ja = Math.max(0, Math.floor((Math.min(ay, by) - y0) / CELDA) - 1);
    const jb = Math.min(H - 1, Math.ceil((Math.max(ay, by) - y0) / CELDA) + 1);

    // ¿Pasa el divisor entre el centro de (i,j) y el de su vecino?
    const corta = (px: number, py: number, qx: number, qy: number) => {
      const l1 = lado(cruz(bx - ax, by - ay, px - ax, py - ay));
      const l2 = lado(cruz(bx - ax, by - ay, qx - ax, qy - ay));
      if (l1 === l2) return false;
      const l3 = lado(cruz(qx - px, qy - py, ax - px, ay - py));
      const l4 = lado(cruz(qx - px, qy - py, bx - px, by - py));
      return l3 !== l4;
    };

    for (let j = ja; j <= jb; j++) {
      for (let i = ia; i <= ib; i++) {
        const px = cx(i), py = cy(j);
        if (i + 1 < W && corta(px, py, cx(i + 1), py)) bloqH[j * W + i] = 1;
        if (j + 1 < H && corta(px, py, px, cy(j + 1))) bloqV[j * W + i] = 1;
      }
    }
  }
  return { bloqH, bloqV };
}

/**
 * Congela el área en reposo de la planta: lo que mide su geometría a ejes antes
 * de que nadie toque una pared. Se corre una sola vez por planta —con una
 * bandera global, la segunda planta leía de más en reposo—.
 *
 * Ya no corrige nada, y eso es el arreglo. Antes guardaba por rótulo una
 * DIFERENCIA contra el plano (`Recinto.ajuste`), calculada sobre la geometría de
 * ese instante y congelada para siempre. Existía porque medir de paño a paño NO
 * da el área que cobra la empresa: la zona de Archicad llega al eje en los
 * tabiques, así que un dormitorio medía 6,74 m² donde el plano rotula 7,88.
 *
 * Medir A EJES disuelve esa razón —el número sale bien solo— y con ella se va un
 * defecto medido: en una lámina que llega con un cuarto abierto, el crudo del
 * reposo es enorme y la diferencia congelada queda muy negativa. Las «Pilas» de
 * Burgos miden 16,85 m² en crudo donde el plano rotula 3,83, o sea un ajuste de
 * −13,02; cerrar esa geometría —que es justo lo que uno va a hacer— dejaba el
 * área en **−3,92 m²**. Un cuarto de área negativa. Eso es lo que reportó
 * Daniel, y no hay forma de taparlo mientras el ajuste sea una diferencia vieja
 * sumada a una geometría nueva.
 *
 * Lo que el plano rotula (`Recinto.plano`) y lo que la empresa tiene autorizado
 * (`Planta.ancla`) se siguen guardando y se pueden mostrar al lado, pero son
 * informativos: el número que manda es el que se mide a ejes. Por eso el
 * absoluto puede no coincidir con la «A. INTERNA» impresa en la lámina —decisión
 * explícita: se calcula como calcula la empresa, no como quedó el cajetín—.
 */
function anclar(planta: Planta, crudoInterior: number): void {
  if (planta.anclado) return;
  planta.referencia = crudoInterior;
  planta.anclado = true;
}

/**
 * La forma de cada región en rectángulos, sacada de la retícula de la
 * inundación.
 *
 * Cada renglón se corta en tramos seguidos de la misma región, y un tramo se
 * pega al de arriba cuando arranca y termina EXACTO en las mismas columnas. Un
 * cuarto rectangular —que es casi todos— sale en un solo rectángulo; una L sale
 * en dos. Es lo que hace que pintar la región tocada sean cinco rectángulos y
 * no las diez mil celdas de 2,5 cm que la componen.
 */
function formas(marca: Int32Array, W: number, H: number, x0: number, y0: number): Map<number, Rect[]> {
  const salida = new Map<number, Rect[]>();
  interface Abierto { n: number; i0: number; i1: number; j0: number; j1: number }
  // El rectángulo se CENTRA en las celdas que cubre, de ahí el medio paso de
  // corrimiento: la celda `i` es el punto `x0 + i·CELDA` para todo el resto del
  // archivo —así se busca a qué región pertenece un rótulo— y si acá se la
  // tratara como el intervalo que arranca en ese punto, el rectángulo quedaría
  // media celda a la derecha de lo que representa. En una franja de UNA celda de
  // ancho eso no es cosmética: su centro redondeaba a la celda vecina, que es
  // muro, y un rótulo puesto ahí caía fuera de la región. Medido en la planta 3
  // de Gyro, en una región de 0,22 m².
  const cerrar = (r: Abierto) => {
    const lista = salida.get(r.n) ?? [];
    lista.push([
      x0 + (r.i0 - 0.5) * CELDA,
      y0 + (r.j0 - 0.5) * CELDA,
      (r.i1 - r.i0 + 1) * CELDA,
      (r.j1 - r.j0 + 1) * CELDA,
    ]);
    salida.set(r.n, lista);
  };

  let abiertos = new Map<string, Abierto>();
  for (let j = 0; j < H; j++) {
    const ahora = new Map<string, Abierto>();
    let i = 0;
    while (i < W) {
      const n = marca[j * W + i];
      if (n < 0) { i++; continue; }
      let k = i;
      while (k + 1 < W && marca[j * W + k + 1] === n) k++;
      const llave = `${n}:${i}:${k}`;
      const sigue = abiertos.get(llave);
      if (sigue) { sigue.j1 = j; ahora.set(llave, sigue); }
      else ahora.set(llave, { n, i0: i, i1: k, j0: j, j1: j });
      i = k + 1;
    }
    for (const [llave, r] of abiertos) if (!ahora.has(llave)) cerrar(r);
    abiertos = ahora;
  }
  for (const r of abiertos.values()) cerrar(r);
  return salida;
}

/** ¿Cae el punto adentro de alguno de los rectángulos de una región? */
export function enCeldas(cs: Rect[], x: number, y: number): boolean {
  return cs.some(([rx, ry, rw, rh]) => x >= rx && x <= rx + rw && y >= ry && y <= ry + rh);
}

/**
 * El área de un área dibujada a mano, en m².
 *
 * Una sola función para los TRES casos, y por eso existe: la dibujada a mano es
 * su rectángulo, la de la varita es la suma de sus celdas, la editada a
 * vértices es su polígono, y multiplicar `w × h` en cualquiera de las dos que
 * tienen forma devolvería la CAJA —que es exactamente el número que estaba
 * mal—. Lo llaman la medición, el rótulo del dibujo, la paleta y la tabla; que
 * lo calcule cada uno por su cuenta es cómo se vuelven a desalinear.
 *
 * El orden de las ramas es el contrato: `vertices` manda sobre `celdas` porque
 * convertir a polígono es lo que deja editar la forma que trajo la varita, y la
 * caja vieja se queda de ancla del rótulo.
 */
export function areaLibre(l: Libre): number {
  if (l.vertices?.length) return areaPoligono(l.vertices);
  if (l.celdas?.length) {
    return l.celdas.reduce((s, [, , w, h]) => s + Math.abs(w * h), 0);
  }
  return Math.abs(l.w * l.h);
}

/** La caja que contiene a un polígono: [x, y, ancho, alto]. */
export function cajaDeVertices(vs: Array<[number, number]>): Rect {
  if (!vs.length) return [0, 0, 0, 0];
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y] of vs) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y);
    x1 = Math.max(x1, x); y1 = Math.max(y1, y);
  }
  return [x0, y0, x1 - x0, y1 - y0];
}

/**
 * Un polígono ortogonal, partido en rectángulos.
 *
 * Es el puente entre la forma NUEVA —vértices, que es lo que el usuario edita—
 * y todo lo que el editor ya sabía hacer con rectángulos: frenar la varita
 * (`zonaEncerrada` recibe `bloqueos: Rect[]`), preguntar si el dedo cayó
 * adentro (`enCeldas`) y dibujar el contorno (`aristas`). Sin esto habría que
 * escribir una segunda versión de cada una de esas tres cosas.
 *
 * El método son FRANJAS horizontales, y es exacto —no una aproximación— porque
 * el polígono es ortogonal: entre dos `y` consecutivas de la lista de vértices
 * no hay ningún vértice, así que ahí la figura es una pila de rectángulos cuyos
 * lados verticales son los que cruzan la franja. Se cortan a la mitad de la
 * franja, se ordenan y se emparejan de dos en dos: adentro, afuera, adentro.
 *
 * Funciona con cóncavos —una L, una T, el lote con el garaje mordido— que es el
 * caso para el que existe.
 */
export function rectangulosDePoligono(vs: Array<[number, number]>): Rect[] {
  if (vs.length < 4) return [];
  const ys = [...new Set(vs.map(([, y]) => y))].sort((a, b) => a - b);
  const salida: Rect[] = [];
  for (let i = 0; i < ys.length - 1; i++) {
    const y0 = ys[i], y1 = ys[i + 1];
    if (y1 - y0 < 1e-6) continue;
    const ym = (y0 + y1) / 2;
    // Los lados que cruzan la franja. Un lado horizontal no la cruza nunca, y
    // uno vertical la cruza si `ym` le cae en medio.
    const cortes: number[] = [];
    for (let j = 0; j < vs.length; j++) {
      const [ax, ay] = vs[j];
      const [bx, by] = vs[(j + 1) % vs.length];
      if (Math.abs(ax - bx) > 1e-6) continue;          // horizontal
      if (Math.min(ay, by) < ym && ym < Math.max(ay, by)) cortes.push(ax);
    }
    cortes.sort((a, b) => a - b);
    // De dos en dos: el tramo entre el primer corte y el segundo está adentro,
    // el que va del segundo al tercero está afuera, y así.
    for (let k = 0; k + 1 < cortes.length; k += 2) {
      const w = cortes[k + 1] - cortes[k];
      if (w > 1e-6) salida.push([cortes[k], y0, w, y1 - y0]);
    }
  }
  return salida;
}

/**
 * Saca los vértices que no dicen nada: los repetidos y los que están en medio
 * de una recta.
 *
 * Hace falta después de cada arrastre. Empujar un lado hasta dejarlo a la
 * altura de su vecino deja tres vértices alineados y un lado de largo cero; si
 * se quedan, el polígono acumula basura y el siguiente toque parte un lado que
 * en pantalla no existe.
 *
 * Nunca baja de cuatro: un polígono de tres vértices ortogonales no existe, y
 * devolver algo degenerado es peor que devolver lo que entró.
 */
export function limpiarAnillo(vs: Array<[number, number]>): Array<[number, number]> {
  let v = vs.filter(([x, y], i) => {
    const [px, py] = vs[(i + vs.length - 1) % vs.length];
    return Math.abs(x - px) > 1e-6 || Math.abs(y - py) > 1e-6;
  });
  if (v.length < 4) return vs;
  // Los alineados, en pasadas: sacar uno puede dejar alineado al siguiente.
  let cambio = true;
  while (cambio && v.length > 4) {
    cambio = false;
    for (let i = 0; i < v.length; i++) {
      const a = v[(i + v.length - 1) % v.length], b = v[i], c = v[(i + 1) % v.length];
      const recto = (Math.abs(a[0] - b[0]) < 1e-6 && Math.abs(b[0] - c[0]) < 1e-6)
                 || (Math.abs(a[1] - b[1]) < 1e-6 && Math.abs(b[1] - c[1]) < 1e-6);
      if (recto) { v = v.filter((_, j) => j !== i); cambio = true; break; }
    }
  }
  return v;
}

/**
 * La misma forma, repartida en la retícula que forman TODAS sus coordenadas.
 *
 * Es el paso previo obligado para sacarle el contorno con `aristas`, y la razón
 * está escrita en `aristas` mismo: ahí una arista se descarta cuando aparece
 * dos veces, y eso sólo pasa si los vecinos comparten la arista EXACTA. Las
 * celdas de la varita son azulejos de una misma retícula y cumplen; dos
 * rectángulos grandes pegados no —el de abajo mide 10 de ancho y el de arriba
 * 6, así que ninguno de los dos bordes aparece repetido y la costura interna se
 * colaba al contorno—. Partir por la retícula de todas las `x` y todas las `y`
 * devuelve el caso al que `aristas` sí sabe resolver.
 */
export function celdasElementales(cs: Rect[]): Rect[] {
  if (!cs.length) return [];
  const xs = [...new Set(cs.flatMap(([x, , w]) => [x, x + w]))].sort((a, b) => a - b);
  const ys = [...new Set(cs.flatMap(([, y, , h]) => [y, y + h]))].sort((a, b) => a - b);
  const salida: Rect[] = [];
  for (let i = 0; i < xs.length - 1; i++) {
    for (let j = 0; j < ys.length - 1; j++) {
      const cx = (xs[i] + xs[i + 1]) / 2, cy = (ys[j] + ys[j + 1]) / 2;
      if (cs.some(([x, y, w, h]) => cx > x && cx < x + w && cy > y && cy < y + h)) {
        salida.push([xs[i], ys[j], xs[i + 1] - xs[i], ys[j + 1] - ys[j]]);
      }
    }
  }
  return salida;
}

/**
 * El anillo de vértices de una forma hecha de rectángulos.
 *
 * Es lo que deja CONVERTIR a polígono editable lo que levantó la varita: la
 * varita entrega celdas y el gesto nuevo trabaja sobre vértices, así que sin
 * esto la L de un plano real se quedaría sin poder tocarse.
 *
 * Camina el contorno de `aristas` encadenando segmento con segmento. Devuelve
 * `null` cuando no puede cerrarlo de una sola vuelta —una forma con un agujero,
 * o dos celdas que sólo se tocan en la esquina—: ahí un anillo mentiría sobre
 * la figura, y el editor prefiere decir que no puede antes que entregar un
 * polígono con el área cambiada.
 */
export function anilloDeCeldas(cs: Rect[]): Array<[number, number]> | null {
  const segs = aristas(celdasElementales(cs));
  if (!segs.length) return null;
  const k = (x: number, y: number) => `${x.toFixed(4)}:${y.toFixed(4)}`;
  const vecinos = new Map<string, Array<[number, number]>>();
  for (const [ax, ay, bx, by] of segs) {
    for (const [p, q] of [[[ax, ay], [bx, by]], [[bx, by], [ax, ay]]] as const) {
      const kk = k(p[0], p[1]);
      if (!vecinos.has(kk)) vecinos.set(kk, []);
      vecinos.get(kk)!.push([q[0], q[1]]);
    }
  }
  // Una esquina con más de dos salidas es justamente el caso ambiguo.
  for (const vs of vecinos.values()) if (vs.length !== 2) return null;
  const arranque = segs.reduce<[number, number]>(
    (m, [ax, ay]) => (ay < m[1] || (ay === m[1] && ax < m[0]) ? [ax, ay] : m),
    [segs[0][0], segs[0][1]],
  );
  const anillo: Array<[number, number]> = [arranque];
  let previo = k(arranque[0], arranque[1]);
  let actual = vecinos.get(previo)![0];
  while (k(actual[0], actual[1]) !== k(arranque[0], arranque[1])) {
    anillo.push(actual);
    const salidas = vecinos.get(k(actual[0], actual[1]));
    if (!salidas) return null;
    const sigue = salidas.find((s) => k(s[0], s[1]) !== previo);
    if (!sigue) return null;
    previo = k(actual[0], actual[1]);
    actual = sigue;
    if (anillo.length > segs.length + 4) return null;      // no cerró
  }
  // Si quedaron segmentos sin recorrer, la forma tiene más de un contorno.
  if (anillo.length < segs.length) {
    const limpio = limpiarAnillo(anillo);
    if (limpio.length < 4) return null;
    // El área tiene que coincidir con la de las celdas o el anillo recorrió
    // sólo una parte.
    const suma = cs.reduce((s, [, , w, h]) => s + Math.abs(w * h), 0);
    if (Math.abs(areaPoligono(limpio) - suma) > 0.005) return null;
    return limpio;
  }
  return limpiarAnillo(anillo);
}

/** La caja que contiene a todos los rectángulos: [x, y, ancho, alto]. */
export function cajaDe(cs: Rect[]): Rect {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const [x, y, w, h] of cs) {
    x0 = Math.min(x0, x); y0 = Math.min(y0, y);
    x1 = Math.max(x1, x + w); y1 = Math.max(y1, y + h);
  }
  return Number.isFinite(x0) ? [x0, y0, x1 - x0, y1 - y0] : [0, 0, 0, 0];
}

/**
 * El CONTORNO de una forma hecha de rectángulos: sus aristas de afuera.
 *
 * Hace falta para dibujarla. Pintar cada rectángulo con su propio borde
 * punteado dejaría las costuras de adentro a la vista y una L se leería como
 * dos áreas pegadas, que es lo contrario de lo que la varita acaba de resolver.
 *
 * Una arista es interna cuando aparece DOS veces —una por cada rectángulo que
 * la comparte— y de afuera cuando aparece una sola. Vale porque los
 * rectángulos salen todos de la misma retícula: los vecinos comparten la arista
 * exacta, no media.
 */
export function aristas(cs: Rect[]): Array<[number, number, number, number]> {
  const cuenta = new Map<string, number>();
  const llave = (a: number, b: number, c: number, d: number) =>
    `${a.toFixed(4)}:${b.toFixed(4)}:${c.toFixed(4)}:${d.toFixed(4)}`;
  for (const [x, y, w, h] of cs) {
    for (const e of [
      [x, y, x + w, y], [x, y + h, x + w, y + h],
      [x, y, x, y + h], [x + w, y, x + w, y + h],
    ] as const) {
      const k = llave(e[0], e[1], e[2], e[3]);
      cuenta.set(k, (cuenta.get(k) ?? 0) + 1);
    }
  }
  const salida: Array<[number, number, number, number]> = [];
  for (const [x, y, w, h] of cs) {
    for (const e of [
      [x, y, x + w, y], [x, y + h, x + w, y + h],
      [x, y, x, y + h], [x + w, y, x + w, y + h],
    ] as const) {
      if (cuenta.get(llave(e[0], e[1], e[2], e[3])) === 1) salida.push([...e]);
    }
  }
  return salida;
}

/**
 * Reparte el reposo de las regiones de ANTES entre las de AHORA.
 *
 * Es la contabilidad de partir y juntar un área, y hace falta porque el reposo
 * se indexa por NOMBRE: partir «Sala-Comedor» en dos estrena dos nombres que no
 * tienen reposo, y mostrarlos contra 0 diría que la casa ganó 18 m² cuando no se
 * movió un ladrillo. Dividir un área es ponerle dos nombres a lo mismo, así que
 * el delta —que es lo que fija el precio— tiene que quedar en cero.
 *
 * Una sola regla cubre los dos sentidos, porque lo que se mira es quién está
 * adentro de quién: al PARTIR, la madre contiene a sus dos hijas y su reposo se
 * reparte entre ellas en proporción a lo que miden; al JUNTAR, las dos mitades
 * caen dentro de la región nueva y sus reposos se le suman. Así partir y volver
 * a juntar devuelve exactamente el número de antes.
 *
 * El parentesco se prueba en LAS DOS DIRECCIONES, y eso no es cinturón y
 * tirantes: cada región tiene UN punto que la representa, así que al juntar dos
 * mitades el punto de la región nueva cae en una sola de ellas y la otra se
 * quedaba sin heredar. Medido: juntar «Sala» y «Comedor» devolvía 10,91 m² de
 * reposo donde iban 21,71, y el cuarto aparecía con +10,80 m² de regalo.
 */
export function repartirReposo(
  antes: Region[], viejo: Record<string, number> | null, med: Medicion,
): Record<string, number> {
  const nuevo: Record<string, number> = {};
  const toco = new Set<string>();
  for (const a of antes) {
    const dentro = med.regiones.filter(
      (g) => enCeldas(a.celdas, g.x, g.y) || enCeldas(g.celdas, a.x, a.y),
    );
    if (!dentro.length) continue;              // la región se abrió al exterior
    const r = viejo?.[a.nombre] ?? a.referencia;
    const suma = dentro.reduce((s, g) => s + g.area, 0);
    for (const g of dentro) {
      nuevo[g.nombre] = (nuevo[g.nombre] ?? 0) + (suma > 0 ? r * (g.area / suma) : 0);
      toco.add(g.nombre);
    }
  }
  // Una región que no heredó de ninguna es nueva de verdad —apareció al abrir
  // una pared—. Se la deja en reposo contra sí misma: decir 0 la mostraría
  // entera como ganancia, que es lo contrario de lo que pasó.
  for (const g of med.regiones) if (!toco.has(g.nombre)) nuevo[g.nombre] = g.area;
  return nuevo;
}

/**
 * La planta COMO QUEDA en esa vista: lo que la vista no muestra, no existe.
 *
 * Es toda la implementación de los estados de rehabilitación del lado de la
 * medición, y es a propósito que `medirPlanta` no se enteró de que existen: una
 * pared demolida no es un caso especial a contemplar en la inundación, es una
 * pared que no está. Filtrar antes en vez de condicionar adentro deja el motor
 * con una sola regla.
 *
 * Decisión de Daniel el 10-oct-2026, sobre la alternativa de que ocultar fuera
 * sólo cosmético: «es lo que hace un plano de remodelación de verdad: tumbar un
 * muro junta dos cuartos y el número lo dice».
 *
 * ⚠️ Devuelve la MISMA planta cuando no hay nada que sacar, y eso no es una
 * optimización: `medirPlanta` ESCRIBE el anclaje sobre lo que recibe, y sobre
 * una copia esa escritura se perdería. Una planta sin nada marcado es entera
 * «existente», así que los 50 modelos publicados siguen por el camino de
 * siempre, con el mismo objeto.
 *
 * Las regiones cerradas se van por `omitidas` y no borrándolas, porque no son
 * objetos que se puedan borrar: ver `Planta.estados`.
 */
export function plantaSegunVista(planta: Planta, vista: VistaObra): Planta {
  const ver = <T extends { estado?: Estado }>(xs: T[] | undefined) =>
    xs?.filter((x) => seVe(x.estado, vista));
  // Los vanos se filtran DENTRO de su pared: un vano que no se ve deja la pared
  // cerrada ahí, que es lo que hay que ver en «Estado actual» —la puerta
  // todavía no se abrió—. No mueve ningún área, porque la inundación tapa los
  // vanos a propósito; es el único filtro de los seis que es dibujo puro.
  const conVanos = (w: Pared): Pared => {
    const vs = w.vanos.filter((v) => seVe(v.estado, vista));
    return vs.length === w.vanos.length ? w : { ...w, vanos: vs };
  };
  const paredes = ver(planta.paredes)!.map(conVanos);
  const libres = ver(planta.libres);
  const divisores = ver(planta.divisores);
  const muebles = ver(planta.muebles);
  const cotas = ver(planta.cotas);
  const estados = planta.estados ?? {};
  const ocultas = Object.keys(estados).filter((n) => !seVe(estados[n], vista));
  const intacta = !ocultas.length
    && paredes.length === planta.paredes.length
    // Identidad y no sólo cantidad: `conVanos` devuelve una pared NUEVA cuando
    // le quitó un vano, y con sólo contar paredes eso pasaba desapercibido.
    && paredes.every((w, i) => w === planta.paredes[i])
    && libres?.length === planta.libres?.length
    && divisores?.length === planta.divisores?.length
    && muebles?.length === planta.muebles?.length
    && cotas?.length === planta.cotas?.length;
  if (intacta) return planta;
  return {
    ...planta,
    paredes, libres, divisores, muebles, cotas,
    omitidas: ocultas.length ? [...(planta.omitidas ?? []), ...ocultas] : planta.omitidas,
  };
}

/**
 * Mide lo que esa vista muestra, anclando siempre contra el estado final.
 *
 * Las dos mitades de la frase son dos decisiones distintas. Que la vista mida es
 * lo que pidió Daniel. Que el ANCLA no siga a la vista es consecuencia de que
 * sólo cuatro de las siete son un edificio: `inicial`/`actual` es lo que está
 * parado hoy y `planteado`/`final` lo que va a quedar; `nuevo` y `demoler` son
 * listas de trabajo y `todo` superpone dos momentos que nunca coexisten. Un
 * reposo congelado desde «Demoliciones» dejaría el delta —y con él el precio del
 * extra— clavado contra media planta, y para siempre: el anclaje ocurre UNA vez.
 *
 * Se pregunta por `mismaGeometria` y no por `vista === 'final'` porque desde el
 * 10-oct-2026 hay DOS vistas con la geometría del estado final: la teñida y la
 * limpia. Son la misma planta recortada, así que miden igual.
 */
export function medirSegunVista(planta: Planta, vista: VistaObra): Medicion {
  const fin = plantaSegunVista(planta, 'final');
  const comoElAncla = mismaGeometria(vista, 'final');
  if (fin !== planta && (!planta.anclado || planta.reposo == null)) {
    const m = medirPlanta(fin);
    planta.referencia = fin.referencia;
    planta.reposo = fin.reposo;
    planta.anclado = fin.anclado;
    if (comoElAncla) return m;      // ya está medido: no hay por qué inundar dos veces
  }
  return medirPlanta(comoElAncla ? fin : plantaSegunVista(planta, vista));
}

/** Mide la planta tal como está. No la modifica salvo el anclaje inicial. */
export function medirPlanta(planta: Planta): Medicion {
  // Las áreas libres se miden por su propia geometría: no hay nada que inundar
  // ni nada que anclar, son un rectángulo dibujado a mano. Por eso se calculan
  // antes de la salida corta —una planta sin paredes igual puede tener una
  // terraza dibujada, y callarla sería perderla—.
  const libres = (planta.libres ?? []).map((l) => ({
    id: l.id, nombre: l.nombre, area: areaLibre(l),
    // Ausente = externa: es para lo que nacieron.
    clase: l.clase ?? ('externa' as Clase),
  }));
  // Una suma por clase, por tabla y no por filtro repetido: cuatro columnas son
  // cuatro `filter` encadenados y el quinto tipo que entre quedaría afuera en
  // silencio, que es el defecto que esto evita.
  const porClase = (xs: Array<{ area: number; clase: Clase }>): Record<Clase, number> => {
    const s: Record<Clase, number> = { interna: 0, externa: 0, lote: 0, azotea: 0 };
    for (const x of xs) s[x.clase] += x.area;
    return s;
  };
  const sl = porClase(libres);

  if (!planta.paredes.length) {
    return {
      regiones: [], sueltos: [], total: sl.interna, referencia: 0, delta: sl.interna,
      libres, interna: sl.interna, externa: sl.externa, lote: sl.lote, azotea: sl.azotea,
    };
  }
  const ws = sellar(planta.paredes);

  let bx0 = Infinity, by0 = Infinity, bx1 = -Infinity, by1 = -Infinity;
  for (const w of ws) {
    const k = caja(w);
    bx0 = Math.min(bx0, k[0]); by0 = Math.min(by0, k[1]);
    bx1 = Math.max(bx1, k[2]); by1 = Math.max(by1, k[3]);
  }
  /**
   * Anillo libre alrededor de la planta, para que el EXTERIOR sea una sola
   * región.
   *
   * No es holgura por gusto: el exterior se reconoce por contener la celda 0, y
   * midiendo a ejes los ejes de las paredes de fachada llegan justo al borde de
   * la caja —`caja()` la abre media pared y la punta del eje se estira otra
   * media—. Sin anillo, esos ejes parten la franja de afuera en cuatro tiras:
   * una se queda con la celda 0 y las otras tres pasan por interior. Medido en
   * una caja de 4 × 5 m: sumaba 21,60 m² donde van 20,00.
   *
   * Cuatro celdas dejan margen de sobra incluso para el `PUNTA` que `barreras`
   * le agrega a cada divisor, y cuestan ocho columnas de retícula.
   */
  const MARGEN = 4 * CELDA;
  const x0 = bx0 - MARGEN, y0 = by0 - MARGEN;
  const W = Math.ceil((bx1 + MARGEN - x0) / CELDA) + 2;
  const H = Math.ceil((by1 + MARGEN - y0) / CELDA) + 2;

  /**
   * Las paredes se miden A EJES y no a paño, que es como calcula la empresa.
   *
   * El mecanismo es el mismo que ya usaban los divisores: el eje de cada pared
   * entra como una BARRERA —corta el paso entre dos celdas vecinas sin ocupar
   * ninguna— en vez de pintarse como masa. Las dos regiones que separa se tocan
   * en el eje, así que cada una se queda con la mitad del grosor y nada de la
   * casa se pierde. Pintar el muro, que es lo obvio, le daba a cada cuarto su
   * área de paño a paño y obligaba a corregirla después contra el plano; ver
   * `anclar` para el agujero que abría esa corrección.
   *
   * Dos detalles que no son cosmética:
   *
   * - Los vanos NO se descuentan, a propósito: una puerta no junta dos cuartos
   *   en uno para efectos de área.
   * - Cada punta se estira media pared (`e/2`), que es justo la tolerancia que
   *   antes regalaba el grosor pintado. Dos paredes que deberían cruzarse y
   *   quedan cortas por la extracción seguían cerrando porque sus masas se
   *   tocaban; un eje pelado dejaría escapar la inundación por el pelo que
   *   falta. `sellar` ya estira hasta `q.c`, que es el eje, así que la limpieza
   *   de esquinas sigue valiendo tal cual.
   */
  const valla = barreras([...ejesDe(ws), ...(planta.divisores ?? [])], W, H, x0, y0);

  // Inundación de todas las celdas libres. El EXTERIOR es la región que contiene
  // la celda 0, no «la que toca el borde»: esto último deja en cero las plantas
  // cuya envolvente se abre a la cochera.
  const marca = new Int32Array(W * H).fill(-1);
  const pila = new Int32Array(W * H);
  const crudos: number[] = [];
  const esFuera: boolean[] = [];
  const centros: Array<[number, number]> = [];
  for (let s = 0; s < W * H; s++) {
    if (marca[s] >= 0) continue;
    const n = crudos.length;
    let sp = 0, cnt = 0, fuera = false, si = 0, sj = 0;
    pila[sp++] = s; marca[s] = n;
    while (sp) {
      const p = pila[--sp];
      cnt++;
      if (p === 0) fuera = true;
      const pi = p % W;
      const pj = (p - pi) / W;
      si += pi; sj += pj;
      for (const q of [p - 1, p + 1, p - W, p + W]) {
        if (q < 0 || q >= W * H) continue;
        if (Math.abs((q % W) - pi) > 1) continue;   // no saltar de renglón
        if (marca[q] >= 0) continue;
        // Ni una pared ni un divisor ocupan celdas: cierran el paso entre estas
        // dos. Es lo único que separa una región de la vecina.
        if (valla) {
          const cerrado =
            q === p + 1 ? valla.bloqH[p]
            : q === p - 1 ? valla.bloqH[q]
            : q === p + W ? valla.bloqV[p]
            : valla.bloqV[q];
          if (cerrado) continue;
        }
        marca[q] = n;
        pila[sp++] = q;
      }
    }
    crudos.push(cnt * CELDA * CELDA);
    esFuera.push(fuera);
    // El centro de masa queda de respaldo: donde se escribe de verdad lo
    // decide `adentro`, que no se sale de la región (ver más abajo).
    centros.push([x0 + (si / cnt) * CELDA, y0 + (sj / cnt) * CELDA]);
  }

  // Cada región se nombra con los rótulos que le caen adentro.
  for (const r of planta.recintos) {
    r.region = null;
    const i = Math.round((r.x - x0) / CELDA);
    const j = Math.round((r.y - y0) / CELDA);
    if (i < 0 || i >= W || j < 0 || j >= H) continue;
    const n = marca[j * W + i];
    if (n < 0 || esFuera[n]) continue;
    r.region = n;
  }

  const formadas = formas(marca, W, H, x0, y0);
  /**
   * Un punto garantizado ADENTRO de la región: el centro de su rectángulo más
   * grande.
   *
   * No el centro de masa, que es lo que se usaba. En una región en L el centro
   * de masa cae AFUERA —encima de un muro, o adentro del cuarto vecino—, y de
   * ahí salía un defecto medido: al ponerle nombre a una región sin rótulo, el
   * rótulo nuevo aterrizaba fuera de ella y el renombre no surtía efecto. En la
   * planta alta de Salamanca eso le pasaba a la región de 6,82 m².
   */
  const adentro = (n: number): [number, number] => {
    const rs = formadas.get(n) ?? [];
    const g = rs.reduce((m, r) => (r[2] * r[3] > m[2] * m[3] ? r : m), rs[0]);
    return g ? [g[0] + g[2] / 2, g[1] + g[3] / 2] : centros[n];
  };
  const interiores = crudos.map((_, n) => n).filter((n) => !esFuera[n] && crudos[n] >= MINIMA);
  const crudoInterior = interiores.reduce((s, n) => s + crudos[n], 0);
  anclar(planta, crudoInterior);

  const agrupado = new Map<number, typeof planta.recintos>();
  for (const r of planta.recintos) {
    if (r.region == null) continue;
    const lista = agrupado.get(r.region) ?? [];
    lista.push(r);
    agrupado.set(r.region, lista);
  }

  const nombrar = (n: number) =>
    // Si dos recintos comparten región se dicen juntos, en vez de inventar un
    // corte que el plano no tiene.
    [...new Set((agrupado.get(n) ?? []).map((r) => r.nombre))].join(' + ');

  // Cuenta TODO el interior, tenga rótulo o no: la escalera, los clósets y los
  // baños chicos son área cerrada igual, y el croquis del catálogo rotula apenas
  // los cuartos principales. Antes había dos ramas acá —una que sumaba sólo lo
  // rotulado, porque la suma de los rótulos ERA el área autorizada— y a ejes
  // sobra: ya no se persigue el número del cajetín, se mide la casa.
  const regiones: Region[] = interiores.map((n) => {
    // Lo que diría un plano se calcula UNA vez y se guarda aparte: de ahí sale
    // `nombre` —la llave del editor, con sus rellenos— sin que la lámina tenga
    // que adivinar después cuál de los dos nombres es un invento nuestro.
    const rotulo = nombrar(n);
    return {
      nombre: rotulo || 'Sin rótulo',
      rotulo,
      area: crudos[n],
      referencia: 0,
      x: agrupado.get(n)?.[0].x ?? adentro(n)[0],
      y: agrupado.get(n)?.[0].y ?? adentro(n)[1],
      indice: n,
      celdas: formadas.get(n) ?? [],
      // Provisoria: la de verdad se resuelve abajo, cuando los nombres
      // repetidos ya están numerados — `clases` se indexa por el nombre FINAL.
      clase: 'interna' as const,
    };
  });

  // Los nombres repetidos se numeran. No es cosmética: el reposo se indexa por
  // NOMBRE, y con tres regiones llamadas «Sin rótulo» la última pisaba a las dos
  // anteriores — en reposo la planta mostraba +4,11 y +5,28 sin que nadie
  // hubiera movido una pared.
  const cuantos = new Map<string, number>();
  for (const g of regiones) cuantos.set(g.nombre, (cuantos.get(g.nombre) ?? 0) + 1);
  const van = new Map<string, number>();
  for (const g of regiones) {
    if ((cuantos.get(g.nombre) ?? 0) < 2) continue;
    const n = (van.get(g.nombre) ?? 0) + 1;
    van.set(g.nombre, n);
    g.nombre = `${g.nombre} ${n}`;
  }

  // Las regiones que el usuario mandó a contar como una sola se fusionan ACÁ:
  // después de numerar —porque `grupos` se indexa por el nombre final— y antes
  // del reposo, para que el grupo tenga UN reposo y UN delta en vez de arrastrar
  // los de sus partes.
  //
  // El área del grupo es la suma exacta de las partes: unir no inventa ni pierde
  // un centímetro, sólo deja de mostrarlas por separado. La forma es la unión de
  // sus celdas, así el grupo se pinta y se toca como un cuarto solo.
  const fundidas: Region[] = [];
  const porGrupo = new Map<string, Region>();
  for (const g of regiones) {
    const grupo = planta.grupos?.[g.nombre];
    if (!grupo) { fundidas.push(g); continue; }
    const ya = porGrupo.get(grupo);
    if (!ya) {
      // El nombre del grupo lo puso el usuario, así que ése SÍ va a la lámina:
      // `rotulo` lo sigue en vez de arrastrar el de la primera parte.
      const cabeza = { ...g, nombre: grupo, rotulo: grupo };
      porGrupo.set(grupo, cabeza);
      fundidas.push(cabeza);
      continue;
    }
    ya.area += g.area;
    ya.celdas = [...ya.celdas, ...g.celdas];
  }
  // Un grupo de UNA sola región no es un grupo: la otra parte se fue cuando el
  // usuario movió una pared, y dejarlo renombraría un cuarto solo por el nombre
  // del grupo. Se le devuelve su nombre.
  for (const [grupo, g] of porGrupo) {
    const partes = regiones.filter((r) => planta.grupos?.[r.nombre] === grupo).length;
    if (partes >= 2) continue;
    const sola = regiones.find((r) => r.indice === g.indice);
    g.nombre = sola?.nombre ?? grupo;
    g.rotulo = sola?.rotulo ?? grupo;
  }
  // Las que el usuario sacó del cómputo se van de la lista entera: de la tabla,
  // de las dos sumas y del delta. No quedan en `externa`, que SÍ suma.
  const omitidas = new Set(planta.omitidas ?? []);
  regiones.length = 0;
  regiones.push(...fundidas.filter((g) => !omitidas.has(g.nombre)));

  // El reposo se captura en la primera medición, que es la planta sin tocar.
  if (planta.reposo == null) {
    planta.reposo = Object.fromEntries(regiones.map((g) => [g.nombre, g.area]));
  }
  // Una región que no existía en reposo —el usuario partió un cuarto en dos— no
  // tiene con qué compararse, y decir 0 es más honesto que repartir a ojo.
  for (const g of regiones) g.referencia = planta.reposo[g.nombre] ?? 0;
  // La clase se resuelve acá, con el nombre ya numerado: una región cerrada es
  // interna salvo que el usuario la haya marcado al revés.
  for (const g of regiones) g.clase = planta.clases?.[g.nombre] ?? 'interna';

  // Las cuatro columnas: cada una es sus regiones más sus áreas dibujadas a
  // mano. Sólo `interna` alimenta el total, la referencia y el delta.
  const sr = porClase(regiones);
  const interna = sr.interna + sl.interna;
  const externa = sr.externa + sl.externa;
  const lote = sr.lote + sl.lote;
  const azotea = sr.azotea + sl.azotea;
  // Lo que NO es interna, sea cual sea su clase: es a lo que hay que sacarle la
  // referencia (ver el comentario de abajo). Se escribe por negación y no
  // listando las otras tres para que una quinta clase futura entre sola.
  const fuera = regiones.filter((g) => g.clase !== 'interna');

  /**
   * La referencia se congela en el anclaje, no se recalcula sobre las regiones
   * de ahora: si el usuario abre un cuarto al exterior, el área de ese cuarto se
   * pierde del total y eso TIENE que verse en el delta.
   *
   * Lo que sí se le descuenta es el reposo de las regiones que el usuario sacó
   * del lado interno —a externa, a lote o a azotea, da igual cuál—, y eso es la
   * regla que hace que CATEGORIZAR no valga plata: marcar la cochera como
   * externa le saca su área al lado interno, y sin sacarle también su
   * referencia aparecería un delta de −12 m² —un descuento inventado— sin que
   * nadie hubiera movido una pared. Reclasificar mueve el área Y su referencia
   * al mismo tiempo.
   *
   * Con las áreas dibujadas a mano es al revés a propósito: nunca tuvieron
   * referencia, así que mandar una terraza para adentro SÍ suma al delta. Y
   * tiene que sumar: eso es exactamente el metro cuadrado nuevo que se cobra.
   */
  const referencia = (planta.referencia ?? 0) - fuera.reduce((s, g) => s + g.referencia, 0);

  return {
    regiones,
    sueltos: planta.recintos.filter((r) => r.region == null).map((r) => r.nombre),
    total: interna,
    referencia,
    delta: interna - referencia,
    libres,
    interna,
    externa,
    lote,
    azotea,
  };
}

/**
 * El rectángulo que las paredes encierran alrededor de un punto — la «varita
 * mágica» que pidió Daniel para no dibujar a pulso.
 *
 * Existe porque dibujar el rectángulo a mano es justo lo que no se puede hacer
 * bien en un celular con el cliente enfrente: la cochera, la terraza y las pilas
 * son rectángulos cuyos lados YA están puestos —son paredes de la casa— y
 * copiarlos a dedo deja un área que se parece pero no cierra con nada.
 *
 * Por qué no alcanza la inundación, que ya encuentra los espacios cerrados: los
 * casos que importan están ABIERTOS por un lado (la cochera sin pared de frente,
 * la terraza bajo el alero). Para la inundación eso es exterior; para la venta es
 * un área. Así que la varita no inunda: tira cuatro rayos ortogonales desde el
 * punto y se detiene en la primera pared que lo tapa.
 *
 * Dos decisiones que importan:
 *
 * - **Se detiene en el EJE de la pared, no en su cara.** Es la misma regla con
 *   la que se mide todo lo demás desde que Daniel eligió ejes puros, así que el
 *   área que sale es comparable con las de las regiones cerradas. Parar en la
 *   cara daría un área sistemáticamente menor y las dos listas no se podrían
 *   sumar entre sí.
 * - **Los vanos NO abren paso.** Se usa el largo completo de la pared y no sus
 *   `tramos()` macizos: una pared con una puerta sigue encerrando el cuarto. Si
 *   el rayo se colara por el vano, el área se iría hasta la pared del cuarto
 *   vecino y la varita devolvería dos cuartos cada vez que apuntara cerca de una
 *   puerta.
 *
 * `abiertos` cuenta los lados que ninguna pared cerró; ahí el rectángulo se topa
 * contra la envolvente de los ejes de la planta, que es una decisión del editor y
 * no una medición. Sirve para avisar en vez de entregar un número mudo.
 *
 * `bloqueos` son las áreas que YA están dibujadas. Sus bordes frenan el rayo
 * igual que una pared, y esa es la corrección que pidió Daniel (4-oct-2026):
 * «me seleccionaba todo el espacio entre paredes y realmente debería reconocer
 * que hay un área existente; tomaría el área de lo que limita entre las paredes
 * y el área existente, o sea, con esa línea divisoria». Sin esto la varita
 * devolvía el espacio entero y el área ya dibujada se contaba dos veces.
 *
 * Un bloqueo frena igual que una pared y no al revés —no se resta después— para
 * que el número y el rectángulo dibujado digan lo mismo. Restar el solape daría
 * un área que no coincide con lo que se ve, que es la peor clase de error en una
 * pantalla donde el que vende lee el número y el cliente mira el dibujo.
 *
 * Devuelve `null` si el punto cae dentro de una pared o de un bloqueo, o si no
 * hay paredes.
 */
export function rectEncerrado(
  ws: Pared[],
  p: { x: number; y: number },
  bloqueos: Rect[] = [],
): { rect: Rect; abiertos: number } | null {
  if (!ws.length) return null;
  for (const w of ws) {
    const [x0, y0, x1, y1] = caja(w);
    if (p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1) return null;
  }
  for (const [bx, by, bw, bh] of bloqueos) {
    if (p.x >= bx && p.x <= bx + bw && p.y >= by && p.y <= by + bh) return null;
  }

  // La envolvente de los EJES: el tope cuando de ese lado no hay nada.
  const env = ws.reduce(
    (k, w) => {
      const [a, b] = w.o === 'v' ? [w.c, w.c] : [w.a, w.b];
      const [c, d] = w.o === 'v' ? [w.a, w.b] : [w.c, w.c];
      return {
        x0: Math.min(k.x0, a, b), x1: Math.max(k.x1, a, b),
        y0: Math.min(k.y0, c, d), y1: Math.max(k.y1, c, d),
      };
    },
    { x0: Infinity, x1: -Infinity, y0: Infinity, y1: -Infinity },
  );

  /**
   * El eje de la pared más cercana que TAPA el rayo que sale de `p`.
   *
   * `eje` es el eje sobre el que se avanza, `signo` la dirección. Tapa la que es
   * perpendicular al rayo y cuyo largo cubre la coordenada transversal del
   * punto: una pared que termina antes no encierra nada ahí.
   */
  const topa = (eje: 'x' | 'y', signo: 1 | -1): number | null => {
    const o: Orientacion = eje === 'x' ? 'v' : 'h';
    const desde = eje === 'x' ? p.x : p.y;
    const trans = eje === 'x' ? p.y : p.x;
    let mejor: number | null = null;
    /** Un candidato a tope: su coordenada, y entre qué y qué cubre. */
    const ver = (c: number, de: number, a: number) => {
      if (trans < Math.min(de, a) || trans > Math.max(de, a)) return;
      const d = (c - desde) * signo;
      if (d <= 0) return;
      if (mejor == null || d < (mejor - desde) * signo) mejor = c;
    };
    for (const w of ws) {
      if (w.o !== o) continue;
      ver(w.c, w.a, w.b);
    }
    // Los dos lados del bloqueo que son perpendiculares al rayo. Un área
    // dibujada no tiene espesor ni eje: sus bordes SON la línea divisoria.
    for (const [bx, by, bw, bh] of bloqueos) {
      if (eje === 'x') { ver(bx, by, by + bh); ver(bx + bw, by, by + bh); }
      else { ver(by, bx, bx + bw); ver(by + bh, bx, bx + bw); }
    }
    return mejor;
  };

  const izq = topa('x', -1);
  const der = topa('x', 1);
  const arr = topa('y', -1);
  const aba = topa('y', 1);
  const abiertos = [izq, der, arr, aba].filter((v) => v == null).length;

  const x0 = izq ?? env.x0;
  const x1 = der ?? env.x1;
  const y0 = arr ?? env.y0;
  const y1 = aba ?? env.y1;
  if (x1 - x0 <= 0 || y1 - y0 <= 0) return null;
  return { rect: [x0, y0, x1 - x0, y1 - y0], abiertos };
}

/**
 * La forma REAL del espacio que rodea a un punto — la varita de verdad.
 *
 * `rectEncerrado` entregaba su rectángulo y eso estaba mal, lo dijo Daniel el
 * 4-oct-2026 con la foto al lado: «no reconoció la forma, como tuvo que haber
 * cortado en varias partes». Un toque en el Dormitorio I le devolvía 20,01 m²
 * (2,90 × 6,90) con el W.C., el S.S. y la escalera adentro. El rayo se detiene
 * en la primera pared que TAPA el punto, así que un tabique que no cubre esa
 * coordenada no frena nada y la caja se come al vecino. Para un cuarto en L no
 * hay rectángulo que diga la verdad.
 *
 * Así que la varita ya no entrega la caja: INUNDA. Y las dos piezas son
 * necesarias, cada una arregla lo que la otra no puede:
 *
 * - La **inundación** respeta las paredes de adentro con las mismas barreras de
 *   eje con las que se mide la casa (`ejesDe` sobre `sellar`), así que el
 *   espacio sale en L, en T o como sea, y los cuartos cerrados del vecindario
 *   quedan afuera solos: estar cerrado es justamente no dejar pasar.
 * - La **caja de los cuatro rayos** acota el avance. Sin ella la inundación se
 *   escapa por el lado abierto —que es la razón por la que la varita existe: la
 *   cochera sin frente, la terraza bajo el alero— y se lleva la calle.
 *
 * Las áreas ya dibujadas (`bloqueos`) se sacan del juego por azulejo, no por
 * borde. Eso además borra un aviso: cuando esto era un rectángulo podía quedar
 * solape en una esquina y había que avisar «pisa X m² de un área ya dibujada».
 * Inundando no hay solape posible.
 *
 * La retícula es la misma de 2,5 cm de `medirPlanta`, a propósito: el número que
 * sale queda con la misma resolución que el de un cuarto cerrado, y los dos se
 * suman en la misma tabla.
 *
 * Devuelve `null` en los mismos casos que `rectEncerrado` —el punto cayó en una
 * pared, en un área ya dibujada, o no hay nada que cierre— más uno nuevo: que la
 * inundación no haya alcanzado un solo azulejo.
 */
export function zonaEncerrada(
  ws: Pared[],
  p: { x: number; y: number },
  bloqueos: Rect[] = [],
  divisores: Divisor[] = [],
): { celdas: Rect[]; area: number; abiertos: number } | null {
  const tope = rectEncerrado(ws, p, bloqueos);
  if (!tope) return null;
  const [bx, by, bw, bh] = tope.rect;
  const W = Math.max(1, Math.round(bw / CELDA));
  const H = Math.max(1, Math.round(bh / CELDA));
  /**
   * El centro del primer azulejo.
   *
   * Acá la retícula EMBALDOSA la caja —el azulejo `i` va de `bx + i·CELDA` a
   * `bx + (i+1)·CELDA`— en vez de centrarse en los nodos como la de
   * `medirPlanta`. Es lo que hace que `W·H·CELDA²` dé la caja exacta: con celdas
   * centradas sobran media columna y media fila, y una caja de 2,90 m medía
   * 2,925. `barreras` y `formas` cuentan desde el centro del primero, de ahí el
   * medio paso.
   */
  const ox = bx + CELDA / 2;
  const oy = by + CELDA / 2;
  const valla = barreras([...ejesDe(sellar(ws)), ...divisores], W, H, ox, oy);

  const marca = new Int32Array(W * H).fill(-1);
  for (let j = 0; j < H; j++) {
    for (let i = 0; i < W; i++) {
      const cx = ox + i * CELDA, cy = oy + j * CELDA;
      const pisa = bloqueos.some(([rx, ry, rw, rh]) =>
        cx >= rx && cx <= rx + rw && cy >= ry && cy <= ry + rh);
      if (pisa) marca[j * W + i] = -2;
    }
  }

  const i0 = Math.min(W - 1, Math.max(0, Math.floor((p.x - bx) / CELDA)));
  const j0 = Math.min(H - 1, Math.max(0, Math.floor((p.y - by) / CELDA)));
  const arranque = j0 * W + i0;
  if (marca[arranque] === -2) return null;

  const pila = new Int32Array(W * H);
  let sp = 0, cnt = 0;
  marca[arranque] = 0;
  pila[sp++] = arranque;
  while (sp) {
    const s = pila[--sp];
    cnt++;
    const si = s % W;
    for (const q of [s - 1, s + 1, s - W, s + W]) {
      if (q < 0 || q >= W * H) continue;
      if (Math.abs((q % W) - si) > 1) continue;   // no saltar de renglón
      if (marca[q] !== -1) continue;
      if (valla) {
        const cerrado =
          q === s + 1 ? valla.bloqH[s]
          : q === s - 1 ? valla.bloqH[q]
          : q === s + W ? valla.bloqV[s]
          : valla.bloqV[q];
        if (cerrado) continue;
      }
      marca[q] = 0;
      pila[sp++] = q;
    }
  }

  const celdas = formas(marca, W, H, ox, oy).get(0) ?? [];
  if (!celdas.length) return null;
  return { celdas, area: cnt * CELDA * CELDA, abiertos: tope.abiertos };
}
